import React, {
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  makeStyles,
  CircularProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  TableSortLabel
} from '@material-ui/core';
import { useNotify } from 'react-admin';
import classNames from 'classnames';
import moment from 'moment';

const useStyles = makeStyles({
  container: {
    border: '1px solid #E0E0E0',
    borderRadius: '2px',
    flex: 1,
    overflow: 'auto',
    width: '100%'
  },
  containerNoData: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    display: 'flex'
  },
  textNoData: {
    color: '#757575',
    fontSize: '14px',
    textAlign: 'center'
  },
  textNoDataMaximumReached: {
    color: '#ffa500',
    fontSize: '14px',
    textAlign: 'center'
  },
  circularProgress: {
    marginRight: '4px'
  },
  table: {
    border: 'none'
  },
  tableCellHead: {
    borderTop: 'none',
    position: 'sticky',
    top: 0,
    '&:first-child': {
      borderLeft: 'none'
    },
    '&:last-child': {
      borderRight: 'none'
    }
  },
  tableCellBody: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  pagination: {
    width: '100%'
  }
});

/**
 * Generic field compare function
 * @param {*} a 1st value
 * @param {*} b 2nd value
 * @param {*} orderBy Order by field
 * @param {*} columnDataTypes Data types for each columns
 */
function desc(a, b, orderBy, columnDataTypes) {
  const valA = a[orderBy];
  const valB = b[orderBy];
  const currentDataType = columnDataTypes[orderBy];

  // Handle equality
  if (valA === valB) {
    return 0;
  }
  // Handle nulls and undefineds
  if (valA === null || valA === undefined) {
    return 1;
  }
  if (valB === null || valB === undefined) {
    return -1;
  }
  // Compare field values
  if (typeof valA === 'string' && typeof valB === 'string') {
    // Compare between 2 dates
    if (currentDataType && currentDataType === 'date') {
      const dateValA = new Date(valA);
      const dateValB = new Date(valB);
      return dateValB < dateValA ? -1 : 1;
    }
    // Special string compare (case insensitive)
    return valB.toLowerCase() < valA.toLowerCase() ? -1 : 1;
  } else {
    if (
      typeof valA === 'object' &&
      typeof valB === 'object' &&
      currentDataType &&
      currentDataType === 'date'
    ) {
      const dateA = new Date(valA.date);
      const dateB = new Date(valB.date);
      return dateB < dateA ? -1 : 1;
    }
    // Generic compare
    return valB < valA ? -1 : 1;
  }
}

/**
 * Use the function material-ui, to sort the data with an order and a function
 * see https://v4-3-3.material-ui.com/components/tables/#sorting-amp-selecting
 * @param {*} array
 * @param {*} cmp
 */
function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

// Sorting function : ascending or descending
function getSorting(order, orderBy, columnDataTypes) {
  return order === 'desc'
    ? (a, b) => desc(a, b, orderBy, columnDataTypes)
    : (a, b) => -desc(a, b, orderBy, columnDataTypes);
}

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
const TZ_PLACEHOLDER = '_TZ_';

/**
 * Format data for a specific column within the report table
 */
const formatValueByColumn = (value, currentColumn) => {
  if (value === null) {
    if (currentColumn && 'numeric' === currentColumn.dataType) {
      return '0';
    } else {
      return null;
    }
  }

  // Handle column specific data format
  if (currentColumn) {
    switch (currentColumn.dataType) {
      case 'numeric':
        return value.toString();
      case 'date':
        if (
          currentColumn.format !== undefined &&
          currentColumn.format.length > 0
        ) {
          const colDate = typeof value === 'object' ? value.date : value;
          const tz = value.timeZone || '';
          const parsedDate = moment.parseZone(colDate);
          if (parsedDate.isValid()) {
            return parsedDate
              .format(currentColumn.format)
              .replace(TZ_PLACEHOLDER, tz);
          }
        }
        break;
      default:
        // Unhandled dataType : fallback on the default formatting (see below)
        break;
    }
  }

  // Hack warning : trying to guess a value is a date to format it  (MM/dd/yyyy)
  if (
    value &&
    typeof value === 'string' &&
    !isNaN(value.charAt(0)) &&
    value.indexOf(':') > 0
  ) {
    const parsedDate = moment.parseZone(value);
    if (parsedDate.isValid()) {
      return parsedDate.format(DEFAULT_DATE_FORMAT);
    }
  }

  // Default, return value
  return value;
};

const colStyles = {};
const emptyObject = {};

/**
 * Get styling for a specific column within the report table
 * return the same object
 */
const getStylesByColumn = column => {
  if (column && column.width) {
    if (!colStyles[column.width]) {
      colStyles[column.width] = {
        width: column.width,
        minWidth: column.width,
        maxWidth: column.width
      };
    }
    return colStyles[column.width];
  }
  return emptyObject;
};

/**
 * fix date format
 * @param {*} data
 * @returns
 */
const fixColumns = data => {
  const cols = data && data.columns ? data.columns : [];
  return cols.map(col => {
    if (col.dataType === 'date') {
      col.format = col.format
        ? col.format
            .replaceAll('y', 'Y')
            .replaceAll('d', 'D')
            .replaceAll('z', 'Z')
        : DEFAULT_DATE_FORMAT;
      col.format = col.format.replace('Z', '[' + TZ_PLACEHOLDER + ']');
    }
    return col;
  });
};

const pageOptions = [25, 50, 100];
const backIconProps = { 'aria-label': 'previous page' };
const nextIconProps = { 'aria-label': 'next page' };

/**
 * Report datagrid component
 * @param {*} props
 */
const ReportDatagrid = props => {
  const notSupportedFilterCriteria =
    'The report contains filter criteria that is not supported. One of the filters used must contain only one value checked/selected.';
  const maximumOutput =
    "The report has reached it's maximum output. Please change your filter selection to get results.";

  const { data, loading, filtersSentAreDifferent } = props;
  const classes = useStyles();
  const tableEl = useRef(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState();
  const [orderBy, setOrderBy] = useState();
  const notify = useNotify();

  // Columns and rows
  // Data types for each column
  const [columns, rows, columnDataTypes] = useMemo(() => {
    const cols = fixColumns(data);
    return [
      cols,
      data && data.data ? data.data : [],
      cols.map(column => column.dataType)
    ];
  }, [data]);

  const [progressCls, cellHeadCls, cellBodyCls, paginationCls] = useMemo(
    () => [
      { root: classes.circularProgress },
      { head: classes.tableCellHead },
      { body: classes.tableCellBody },
      { root: classes.pagination }
    ],
    [classes]
  );

  const handleRequestSort = useCallback(
    index => {
      const isDesc = orderBy === index && order === 'desc';
      setOrder(isDesc ? 'asc' : 'desc');
      setOrderBy(index);
    },
    [orderBy, order]
  );

  const handleChangePage = useCallback(
    (event, newPage) => {
      tableEl.current.scrollTop = 0;
      setPage(newPage);
    },
    [tableEl]
  );

  const handleChangeRowsPerPage = useCallback(event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  }, []);

  // Reset pagination after every filter changes
  // Only when the filters are modified
  useEffect(() => {
    if (loading && filtersSentAreDifferent) {
      setPage(0);
      setRowsPerPage(25);
    }
  }, [loading, filtersSentAreDifferent]);

  useEffect(() => {
    if (!loading && data.rowLimit) {
      notify('ra.notification.report_has_more_data', {
        messageArgs: { row_limit: data.rowLimit }
      });
    }
  }, [loading, data.rowLimit, notify]);

  // Displays loader and loading text
  if (loading) {
    return (
      <div className={classNames(classes.container, classes.containerNoData)}>
        <div className={classes.textNoData}>
          <CircularProgress size={14} classes={progressCls} />
          Generating Report. Please Wait
        </div>
      </div>
    );
  }

  // Displays message when report has more data or bad criteria
  if (data === 'report_has_more_data' || data === 'report_has_bad_criteria') {
    return (
      <div className={classNames(classes.container, classes.containerNoData)}>
        <div className={classes.textNoDataMaximumReached}>
          No data to display.
          <br />
          {data === 'report_has_more_data'
            ? maximumOutput
            : notSupportedFilterCriteria}
        </div>
      </div>
    );
  }

  // Displays an empty state and instructions for displaying data
  if (data.length <= 0) {
    return (
      <div className={classNames(classes.container, classes.containerNoData)}>
        <div className={classes.textNoData}>
          No Data to Display
          <br />
          Select options on top and then click View Report or Download as Excel
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className={classes.container} ref={tableEl}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => {
                return !columns[index].hidden ? (
                  <TableCell
                    key={index}
                    classes={cellHeadCls}
                    sortDirection={orderBy === index ? order : false}
                  >
                    <TableSortLabel
                      id={column.name}
                      active={orderBy === index}
                      direction={order}
                      onClick={() => handleRequestSort(index)}
                    >
                      {column.name}
                    </TableSortLabel>
                  </TableCell>
                ) : null;
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(rows, getSorting(order, orderBy, columnDataTypes))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <TableRow key={index}>
                    {row.map((value, indexValue) => {
                      return columns[indexValue] &&
                        !columns[indexValue].hidden ? (
                        <TableCell
                          id={columns[indexValue].name}
                          key={indexValue}
                          classes={cellBodyCls}
                          style={getStylesByColumn(columns[indexValue])}
                        >
                          {formatValueByColumn(value, columns[indexValue])}
                        </TableCell>
                      ) : null;
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        classes={paginationCls}
        rowsPerPageOptions={pageOptions}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        backIconButtonProps={backIconProps}
        nextIconButtonProps={nextIconProps}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </React.Fragment>
  );
};

ReportDatagrid.defaultProps = {
  data: [],
  loading: false
};

export default ReportDatagrid;
